import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type CompanyAccountsResponse,
  type GetCompanyAccountsPayload,
} from './types';
import {
  zodCompanyAccountsQueryParamsSchema,
  zodCompanyAccountsResponseSchema,
} from './validations';

/**
 * Fetches all company accounts from the API with given search parameters.
 * @param searchParams - The search parameters for fetching company accounts.
 * @returns The company accounts data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_company_accounts_vadmin_company_account_get
 */
export const fetchCompanyAccountsAdmin = async (
  searchParams: GetCompanyAccountsPayload,
) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.COMPANY.ACCOUNT.GET_ALL_ADMIN(), {
        searchParams,
        validate: {
          response: zodCompanyAccountsResponseSchema,
          searchParams: zodCompanyAccountsQueryParamsSchema,
        },
      })
      .json<CompanyAccountsResponse>();

    return (
      response ?? {
        data: [],
        hasNextPage: false,
        paginatedCount: 0,
        totalCount: 0,
      }
    );
  } catch (error) {
    log.error({
      message: 'Error fetching company accounts',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.fetchCompanyAccountsAdmin.error422,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.fetchCompanyAccountsAdmin.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
